<script>
  import { createEventDispatcher, onMount } from "svelte";
  import { apiRequest } from "../utils/api";

  export let generationId = null;
  export let selected = [];

  // Emit `change` (mirrors CategoryPicker's contract) so the parent form can
  // read the selection. Without this the picker only mutated its OWN `selected`
  // prop — passed one-way from RulesPage, never bound — so every allow_list
  // saved with the untouched default `entries: []` (silent data loss).
  const dispatch = createEventDispatcher();

  let categories = [];
  let products = [];
  let loading = true;
  let error = null;
  let projectId = null;

  // Mirror CategoryPicker: tell the parent form when this picker has settled so
  // Save/Create can be gated until then (the container grows from a one-line
  // "Loading…" box to the full list once the async fetches resolve).
  $: dispatch("ready", !loading);
  // LOCKED rule: currency is never hardcoded. Sourced from the project record
  // (found in the /api/projects index payload, which serializes currency_name)
  // rather than a literal "BOWS" — falls back to a generic label only while
  // still loading/on error.
  let currencyName = "currency";

  onMount(async () => {
    await init();
  });

  async function init() {
    if (!generationId) return;
    loading = true;
    try {
      const gen = await apiRequest(`/api/generations/${generationId}`);
      projectId = gen?.project_template_id;
      if (projectId) {
        const tpl = await apiRequest(`/api/templates/${projectId}`);
        projectId = tpl?.project_id;
      }
      if (projectId) {
        // include_deleted=1: an existing rule may already reference a
        // since-soft-deleted product — it must keep rendering (greyed, see
        // isPickable) so the rule doesn't silently lose a visible entry. New
        // selections are still restricted to active products via isPickable.
        let allProjects;
        [categories, products, allProjects] = await Promise.all([
          apiRequest(`/api/projects/${projectId}/product_categories`),
          apiRequest(`/api/projects/${projectId}/products?include_deleted=1`),
          // No GET /api/projects/:id route exists (only index/create/update) —
          // fetch the list and find this project's currency_name in it.
          apiRequest(`/api/projects`)
        ]);
        const project = (allProjects || []).find((p) => p.id === projectId);
        currencyName = project?.currency_name || "currency";
      }
    } catch (e) {
      error = e?.message || "Could not load catalog";
    } finally {
      loading = false;
    }
  }

  // A soft-deleted product may only stay visible if it's part of the current
  // selection (preserving an existing rule's reference); it can't be newly
  // picked. Active products are always pickable.
  function isPickable(product) {
    return !product.is_deleted || isSelected({ type: "product", id: product.id });
  }

  function isSelected(entry) {
    return selected.some(e => e.type === entry.type && e.id === entry.id);
  }

  function toggle(entry) {
    const idx = selected.findIndex(e => e.type === entry.type && e.id === entry.id);
    let entries;
    if (idx >= 0) {
      entries = [...selected];
      entries.splice(idx, 1);
    } else {
      entries = [...selected, entry];
    }
    selected = entries;
    dispatch("change", entries);
  }
</script>

<div class="border border-neutral-300 rounded-md p-2.5 min-h-[240px] max-h-[240px] overflow-y-auto bg-white text-xs" data-testid="allow-list-picker">
  {#if loading}
    <p class="text-neutral-500 text-[0.76rem]">Loading catalog...</p>
  {:else if error}
    <p class="bg-danger-bg text-danger-text border border-danger-border/20 rounded px-2.5 py-1 text-[0.76rem]" role="alert">{error}</p>
  {:else if !projectId}
    <p class="text-neutral-500 text-[0.76rem]">No project found for this generation.</p>
  {:else}
    <div class="mb-3">
      <p class="font-bold text-[0.72rem] text-neutral-600 uppercase tracking-wider mb-1">Categories</p>
      {#if categories.length === 0}
        <p class="text-neutral-500 text-[0.76rem]">No categories</p>
      {/if}
      {#each categories as cat (cat.id)}
        <label class="flex items-center gap-1.5 py-0.5 cursor-pointer text-[0.78rem] text-neutral-800 hover:text-neutral-950" data-testid="allow-list-category-option" data-category-id={cat.id}>
          <input type="checkbox" class="form-checkbox"
            checked={isSelected({ type: "section", id: cat.id })}
            on:change={() => toggle({ type: "section", id: cat.id })} />
          <span>{cat.name} ({cat.product_count} products)</span>
        </label>
      {/each}
    </div>

    <div class="mb-3">
      <p class="font-bold text-[0.72rem] text-neutral-600 uppercase tracking-wider mb-1">Products</p>
      {#if products.length === 0}
        <p class="text-neutral-500 text-[0.76rem]">No products</p>
      {/if}
      {#each products.filter(isPickable) as product (product.id)}
        <label class="flex items-center gap-1.5 py-0.5 cursor-pointer text-[0.78rem] hover:text-neutral-950" class:text-neutral-800={!product.is_deleted} class:text-neutral-400={product.is_deleted} class:line-through={product.is_deleted} data-testid="allow-list-product-option" data-product-id={product.id} data-deleted={product.is_deleted}>
          <input type="checkbox" class="form-checkbox"
            checked={isSelected({ type: "product", id: product.id })}
            on:change={() => toggle({ type: "product", id: product.id })} />
          <span>{product.category}/{product.name} ({product.amount} {currencyName}){#if product.is_deleted} <span class="not-italic font-semibold text-[0.66rem] uppercase tracking-wide text-neutral-400">(deleted)</span>{/if}</span>
        </label>
      {/each}
    </div>

    <p class="text-[0.72rem] text-neutral-500 mt-1 font-medium">{selected.length} selected</p>
  {/if}
</div>
